import React, { useState, useRef, useEffect } from 'react'
import Form from 'react-bootstrap/Form';
import { debounce } from 'lodash';

// redux
import { setBookingForm, setFlightInformation } from '_redux/slices/assistance-bookings/booking-form';
import { useDispatch } from 'react-redux';

// styles
import { Colors } from '_styles';
import moment from 'moment';

// utils
import { PRMCodes } from '_utils/constants';

// single source of truth for the codes recognised when scanning boarding passes
const ASSISTANCE_CODE_REGEX = new RegExp(`(${PRMCodes.join('|')})`);

const HandScanner = () => {
    const textareaRef = useRef(null); // Create a ref for the textarea

    //#region redux
    const dispatch = useDispatch();

    //#endregion

    //#region local state
    const [error, setError] = useState("")
    //#endregion

    //#region useEffect
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.focus(); // Focus the textarea on initial render
        }
    }, []);
    //#endregion

    //#region functions
    const parseBCBPBarcode = (val) => {
        if (!val) {
            console.error("No barcode data provided");
            setError("No barcode data provided")
            return null;
        }
        setError("")

        let rawValue = val;
        const result = {
            passengerName: "",
            flightNumber: "",
            carrierCode: "",
            departureAirport: "",
            arrivalAirport: "",
            seatNumber: "",
            departureDate: ""
        };

        let firstName, secondName;

        const firstRawValueWord = rawValue?.split(" ")?.[0];
        if (firstRawValueWord?.includes("/")) {
            // Determine name extraction method based on format
            const nameSection = rawValue.substring(2, rawValue.indexOf('/')).trim();
            if (nameSection.includes(' ')) {
                // Handle names with multiple words (e.g., "HUENKE VON PODEWIL")
                result.passengerName = nameSection;

                // Split at the first space only
                const spaceIndex = nameSection.indexOf(' ');
                firstName = nameSection.substring(0, spaceIndex).trim();
                secondName = nameSection.substring(spaceIndex + 1).trim();
            } else {
                // Handle simpler names (e.g., "OMAHONY/JOHN.")
                result.passengerName = rawValue.substring(2).split(/[\s.]/)[0].trim();

                firstName = result?.passengerName?.split("/")[1] ?? "";
                secondName = result?.passengerName?.split("/")[0] ?? "";
            }

        } else {
            // split the name section into last name and first + middle names
            const nameParts = rawValue.substring(2).split('/');

            if (nameParts.length >= 2) {
                const lastName = nameParts[0].trim(); // last name (may contain spaces, e.g., "MC GRATH")

                // extract first and middle names from nameParts[1]
                const firstAndMiddleNamesMatch = nameParts?.[1]?.match(/^([A-Z]+ [A-Z]+)/);
                const firstAndMiddleNames = firstAndMiddleNamesMatch ? firstAndMiddleNamesMatch[0].trim() : "";

                // split first and middle names into firstName and middleName
                const firstMiddleParts = firstAndMiddleNames.split(' ');

                firstName = `${firstMiddleParts?.[0] ?? ""} ${firstMiddleParts?.[1] ?? ""}`

                const middleName = firstMiddleParts.slice(1).join(' '); // middle name(s) (e.g., "GAB")

                // combine last name and first name for the full passenger name
                result.passengerName = `${lastName} ${firstName} ${middleName}`.trim();

                secondName = lastName ?? ""; // last name is the second name

            }
        }

        // Regex to extract the flight details
        const re = /([A-Z]{3})([A-Z]{3})([A-Z]{2})\s+(\d+)\s+/;
        const re2 = /([A-Z]{3})([A-Z]{3})([A-Z]{1,3})\s*(\d+)\s*/;

        const match = rawValue?.match(re);
        const match2 = rawValue?.match(re2)

        if (match) {
            result.departureAirport = match?.[1] ?? "";
            result.arrivalAirport = match?.[2] ?? "";
            let scannedFlightNumber = match?.[0] ?? "";
            if (scannedFlightNumber) {
                scannedFlightNumber = scannedFlightNumber.split(" ");
                // replace(/^0+/, '') remove leading zeros
                result.flightNumber = scannedFlightNumber?.[1]?.replace(/^0+/, '') ?? ""
                result.carrierCode = scannedFlightNumber?.[0]?.slice(-2) ?? ""
            };
            // extract departure Date (3-digit number after flight number)
            const departureDateMatch = rawValue?.match(/\s(\d{3})[A-Z]/);
            if (departureDateMatch) {
                const departureDay = parseInt(departureDateMatch?.[1], 10);
                const currentYear = new Date().getFullYear(); // Assume the current year
                const flightDateObj = new Date(currentYear, 0, departureDay);
                result.departureDate = moment(new Date(flightDateObj)).format("YYYY-MM-DD")
            };

            // more precise regex to extract the seat number from the string
            const seatMatch = rawValue?.match(/(\d+[A-Z]\d+[A-Z]\d+)\s+(\d+>)/);
            if (seatMatch) {
                result.seatNumber = seatMatch[1].slice(5, 8).trim(); // Now extracting '47B'
            };
            const assistanceMatch = rawValue?.match(ASSISTANCE_CODE_REGEX);
            const assistanceCode = assistanceMatch ? assistanceMatch[1] : 'None';

            let tempList = []
            let emptyPassengerObj = {
                firstName: firstName ?? "",
                secondName: secondName ?? "",
                assistance: assistanceCode !== "None" ? assistanceCode : "",
                carrierCode: "",
                flightNumber: "",
                referenceNumber: "",
                flightDepartureDate: "",
                dueTime: "",
                prmBio: "",
                originAssistance: true,
                destinationAssistance: true,
                make: "",
                model: "",
                weight: "",
                battery: "",
                width: "",
                height: "",
                equipment: false,
                additionalPassenger: false,
                errors: [],
                seatNumber: ` ${result.seatNumber}`
            }
            tempList.push(emptyPassengerObj);

            dispatch(setBookingForm(tempList))

            let emptyFlightObj = {
                carrierCode: result?.carrierCode ? result?.carrierCode : "",
                flightNumber: result?.flightNumber ? result?.flightNumber : "",
                flightDepartureDate: result?.departureDate ? result?.departureDate : "",
                errors: [],
            }

            dispatch(setFlightInformation(emptyFlightObj))
        } else if (match2) {
            result.departureAirport = match2?.[1] ?? "";
            result.arrivalAirport = match2?.[2] ?? "";
            result.carrierCode = match2?.[3] ?? "";
            result.flightNumber = match2?.[4]?.replace(/^0+/, '') ?? "";
            // extract departure Date (3-digit number after flight number)
            const departureDateMatch = rawValue?.match(/\s(\d{3})[A-Z]/);
            if (departureDateMatch) {
                const departureDay = parseInt(departureDateMatch?.[1], 10);
                const currentYear = new Date().getFullYear(); // Assume the current year
                const flightDateObj = new Date(currentYear, 0, departureDay);
                result.departureDate = moment(new Date(flightDateObj)).format("YYYY-MM-DD")
            }

            // more precise regex to extract the seat number from the string
            const seatMatch = rawValue?.match(/(\d+[A-Z]\d+[A-Z]\d+)\s+(\d+>)/);
            if (seatMatch) {
                result.seatNumber = seatMatch[1].slice(5, 8).trim(); // Now extracting '47B'
            }
            const assistanceMatch = rawValue?.match(ASSISTANCE_CODE_REGEX);
            const assistanceCode = assistanceMatch ? assistanceMatch[1] : 'None';

            let tempList = []
            let emptyPassengerObj = {
                firstName: firstName ?? "",
                secondName: secondName ?? "",
                assistance: assistanceCode !== "None" ? assistanceCode : "",
                carrierCode: "",
                flightNumber: "",
                referenceNumber: "",
                flightDepartureDate: "",
                dueTime: "",
                prmBio: "",
                originAssistance: true,
                destinationAssistance: true,
                make: "",
                model: "",
                weight: "",
                battery: "",
                width: "",
                height: "",
                equipment: false,
                additionalPassenger: false,
                errors: [],
                seatNumber: ` ${result.seatNumber}`
            }
            tempList.push(emptyPassengerObj);

            dispatch(setBookingForm(tempList))

            let emptyFlightObj = {
                carrierCode: result?.carrierCode ? result?.carrierCode : "",
                flightNumber: result?.flightNumber ? result?.flightNumber : "",
                flightDepartureDate: result?.departureDate ? result?.departureDate : "",
                errors: [],
            }

            dispatch(setFlightInformation(emptyFlightObj))
        } else {
            console.error("Failed to parse flight details");
            setError("Failed to parse flight details")
        }
    }

    const parseBCBPBarcode2 = (val) => {

        // ===== STEP 1: INPUT VALIDATION =====
        // Check if the input is valid (not empty and is a string)
        if (!val || typeof val !== 'string') {
            console.error("❌ [parseBCBPBarcode2] Invalid input: No barcode data provided or invalid type");
            setError("No barcode data provided");
            return null;
        }

        // Clear any previous errors and trim whitespace from input
        setError("");
        const rawValue = val.trim();

        // ===== STEP 2: INITIALIZE RESULT OBJECT =====
        // This object will hold all extracted information from the barcode
        const result = {
            passengerName: "",
            flightNumber: "",
            carrierCode: "",
            departureAirport: "",
            arrivalAirport: "",
            seatNumber: "",
            departureDate: ""
        };

        // ===== STEP 3: EXTRACT PASSENGER NAME =====
        // The passenger name comes after the format code (M1) and before the flight information.
        // Name format: LASTNAME/FIRSTNAMEPNR (PNR may be attached directly to first name with no space)
        let firstName = "";
        let secondName = "";

        // Get the first word from the barcode (should contain the name)
        const firstRawValueWord = rawValue?.split(" ")?.[0];

        // Check if the first word contains a '/' separator (indicates LASTNAME/FIRSTNAME format)
        if (firstRawValueWord?.includes("/")) {
            // Extract the last name section (from position 2 to the '/' character)
            // Position 2 is used to skip the "M1" format code at the beginning
            const nameSection = rawValue.substring(2, rawValue.indexOf('/')).trim();

            if (nameSection.includes(' ')) {
                // Handle multi-word last names (e.g., "HUENKE VON PODEWIL")
                // Some passengers have compound last names with spaces
                result.passengerName = nameSection;
                const spaceIndex = nameSection.indexOf(' ');
                firstName = nameSection.substring(0, spaceIndex).trim();
                secondName = nameSection.substring(spaceIndex + 1).trim();
            } else {
                // ===== HANDLE SIMPLE NAMES WITH ATTACHED PNR =====
                // Examples: 
                // - "OMAHONY/JOHN." (first name with trailing period)
                // - "MATUSZEWSKI/MIROSLAWEPLKTSR" (first name + PNR with no space)
                // - "PALANCAREJO/FELE8N4IOR" (first name + PNR containing digit)

                // Get everything from position 2 (skip "M1") to first space
                // This gives us: LASTNAME/FIRSTNAME+PNR (PNR directly attached to first name)
                const fullNameSection = rawValue.substring(2).split(/\s/)[0].trim();

                // Split by '/' to separate last name from first name+PNR
                const nameParts = fullNameSection.split("/");
                secondName = nameParts[0] ?? "";

                // Clean the first name section: remove trailing dots, periods, and spaces
                // Example: "SYLVIANE." becomes "SYLVIANE"
                let afterSlash = (nameParts[1] ?? "").replace(/[.\s]+$/, '');

                // ===== STRATEGY: SEPARATE FIRST NAME FROM PNR =====
                // Challenge: The PNR is directly attached to the first name with NO space separator
                // PNR characteristics:
                // - Typically 5-7 alphanumeric characters
                // - Often contains digits (e.g., "E8N4IOR", "ABC123")
                // - Sometimes all letters (rare: "PLKTSR", "EXIMPN")
                //
                // First name characteristics:
                // - Typically only uppercase letters (A-Z)
                // - Common length: 3-12 characters (e.g., JOE, JOHN, MICHAEL, CHRISTOPHER)

                // STEP 3A: Look for digits in the string (most PNRs contain at least one digit)
                const firstDigitMatch = afterSlash.match(/\d/);
                const firstDigitIndex = firstDigitMatch ? afterSlash.indexOf(firstDigitMatch[0]) : -1;

                if (firstDigitIndex > 0 && firstDigitIndex <= 10) {
                    // ===== CASE 1: DIGIT FOUND - PNR CONTAINS A NUMBER =====
                    // Example: "FELE8N4IOR" - digit '8' at position 4
                    // The digit usually indicates where the PNR starts or is within the PNR

                    let pnrStartIndex = firstDigitIndex;

                    // Check if we should include the character before the digit in the PNR
                    // PNR format often starts with a letter followed by a digit (e.g., "E8N4IOR")
                    if (firstDigitIndex > 0) {
                        const charBeforeDigit = afterSlash[firstDigitIndex - 1];

                        // Decision logic:
                        // - If there's an uppercase letter right before the digit
                        // - AND removing it would still leave at least 3 characters for the first name
                        // - THEN include that letter in the PNR (it's likely part of the PNR code)
                        const isUpperCase = /[A-Z]/.test(charBeforeDigit);
                        const wouldBeAtLeast3Chars = (firstDigitIndex - 1) >= 3;

                        if (isUpperCase && wouldBeAtLeast3Chars) {
                            // Move PNR start back by 1 to include the letter before the digit
                            // Example: "FELE8N4IOR" → firstName="FEL", PNR="E8N4IOR"
                            pnrStartIndex = firstDigitIndex - 1;
                        } else {
                            console.log("❌ [parseBCBPBarcode2] NOT including letter before digit - isUpperCase:", isUpperCase, "wouldBeAtLeast3Chars:", wouldBeAtLeast3Chars);
                        }
                    }

                    // Extract first name (everything before PNR start) and PNR
                    firstName = afterSlash.substring(0, pnrStartIndex);
                    const pnrPart = afterSlash.substring(pnrStartIndex);

                } else {
                    // ===== CASE 2: NO DIGIT FOUND - PNR IS ALL LETTERS OR NO PNR =====
                    // This means either:
                    // 1. Just the first name, no PNR attached (COMMON CASE)
                    //    Example: "ABDULAZIZ" (9 chars) - just a name
                    // 2. PNR is all letters (RARE CASE)
                    //    Example: "MIROSLAWEPLKTSR" (15 chars) - name + letter-only PNR

                    // Decision based on string length:
                    // Most first names are 3-12 characters long
                    // Examples: JOE(3), JOHN(4), MICHAEL(7), ABDULAZIZ(9), CHRISTOPHER(11)

                    if (afterSlash.length <= 12) {
                        // ===== REASONABLE FIRST NAME LENGTH - USE AS IS =====
                        // The string length is within normal first name range
                        // Assume it's just the first name with no PNR attached
                        // Example: "ABDULAZIZ" (9 chars) → firstName="ABDULAZIZ"
                        firstName = afterSlash;

                    } else if (afterSlash.length > 14) {
                        // ===== VERY LONG STRING - ASSUME PNR IS ATTACHED =====
                        // String is too long to be just a first name
                        // Assume last 7 characters are the PNR (all letters)
                        // Example: "MIROSLAWEPLKTSR" (15 chars) → firstName="MIROSLAW", PNR="PLKTSR"
                        firstName = afterSlash.substring(0, afterSlash.length - 7);
                        const pnrPart = afterSlash.substring(afterSlash.length - 7);

                    } else {
                        // ===== EDGE CASE: LENGTH 13-14 =====
                        // Moderately long string, assume last 6 characters are PNR
                        firstName = afterSlash.substring(0, afterSlash.length - 6);
                        const pnrPart = afterSlash.substring(afterSlash.length - 6);
                    }
                }

                // Combine last name and first name for the full passenger name
                result.passengerName = `${secondName}/${firstName}`;
            }
        } else {
            // ===== ALTERNATIVE NAME FORMAT (WITHOUT '/' IN FIRST WORD) =====
            // This handles less common barcode formats where the name structure is different
            // Example formats that might fall into this category

            // Split the name section into last name and first + middle names
            const nameParts = rawValue.substring(2).split('/');

            if (nameParts.length >= 2) {
                // Extract last name (may contain spaces, e.g., "MC GRATH", "VAN DER BERG")
                const lastName = nameParts[0].trim();

                // Extract first and middle names from the section after the '/'
                // Look for pattern of multiple names separated by spaces (e.g., "JOHN PAUL")
                const firstAndMiddleNamesMatch = nameParts?.[1]?.match(/^([A-Z]+ [A-Z]+)/);
                const firstAndMiddleNames = firstAndMiddleNamesMatch ? firstAndMiddleNamesMatch[0].trim() : "";

                // Split first and middle names into separate parts
                const firstMiddleParts = firstAndMiddleNames.split(' ');
                firstName = `${firstMiddleParts?.[0] ?? ""} ${firstMiddleParts?.[1] ?? ""}`.trim();
                const middleName = firstMiddleParts.slice(1).join(' '); // Remaining parts are middle name(s)

                // Combine last name, first name, and middle name for full passenger name
                result.passengerName = `${lastName} ${firstName} ${middleName}`.trim();
                secondName = lastName; // Last name is the second name


                // Fallback: Handle cases where firstName is empty
                // Extract only consecutive letters before any digit
                if (!firstName || firstName.trim() === "") {
                    // Handle edge cases like "FELE8N4IOR" where we need to extract only "FELE"
                    // Extract only consecutive uppercase letters at the start, stop at first digit
                    const lettersOnlyMatch = nameParts?.[1]?.match(/^([A-Z]+)/);
                    if (lettersOnlyMatch) {
                        firstName = lettersOnlyMatch[1].trim();
                    }
                }
            }
        }

        // ===== STEP 4: EXTRACT FLIGHT INFORMATION =====
        // Flight information follows the passenger name and includes:
        // - Departure airport (3-letter IATA code)
        // - Arrival airport (3-letter IATA code)
        // - Carrier code (2-3 alphanumeric characters)
        // - Flight number (up to 4 digits)
        // Format: XXXYYY CC NNNN (e.g., "JFKLHR BA 1234")

        // ===== STEP 4A: DEFINE REGEX PATTERNS FOR FLIGHT INFORMATION =====
        // Different airlines use slightly different formats, so we test multiple patterns
        const patterns = {
            // Pattern 0: Letter carrier + adjacent flight (no space), followed by Julian date
            // Example: "BHXEZY33  061" → BHX (dep), EZY (arr), EZY (carrier), 33 (flight)
            // Use when carrier is 2-3 letters immediately followed by digits (e.g. EZY33) with no space
            letterCarrierAdjacent: /([A-Z]{3})([A-Z]{3})([A-Z]{2,3})(\d{1,4})(?=\s+\d{3}\b)/,

            // Pattern 1: Standard format
            // Example: "JFKLHRBA 1234 " → JFK (departure), LHR (arrival), BA (carrier), 1234 (flight)
            standard: /([A-Z]{3})([A-Z]{3})([A-Z]{2})\s+(\d+)\s+/,

            // Pattern 2: Numeric carrier format
            // Example: "PUJFRA4Y 0003" → PUJ (departure), FRA (arrival), 4Y (carrier), 0003 (flight)
            numericCarrier: /([A-Z]{3})([A-Z]{3})([A-Z0-9]{1,3})\s+(\d+)/,

            // Pattern 3: Alternative format
            // Example: "JEDCAISV0301" → JED (departure), CAI (arrival), SV (carrier), 0301 (flight)
            alternative: /([A-Z]{3})([A-Z]{3})([A-Z]{1,3})\s*(\d+)\s*/
        };

        // ===== STEP 4B: TEST ALL PATTERNS =====
        const matches = {
            letterCarrierAdjacent: rawValue?.match(patterns.letterCarrierAdjacent),
            standard: rawValue?.match(patterns.standard),
            numericCarrier: rawValue?.match(patterns.numericCarrier),
            alternative: rawValue?.match(patterns.alternative)
        };

        // ===== STEP 4C: SELECT THE BEST MATCHING PATTERN =====
        // Prefer letterCarrierAdjacent when carrier+flight are glued (e.g. EZY33) so we get EZY + 33, not Y33 + 33 or 33 + 061
        let match = null;
        let matchType = "";

        if (matches.letterCarrierAdjacent) {
            match = matches.letterCarrierAdjacent;
            matchType = "letterCarrierAdjacent";
        } else if (matches.numericCarrier) {
            match = matches.numericCarrier;
            matchType = "numericCarrier";
        } else if (matches.standard) {
            match = matches.standard;
            matchType = "standard";
        } else if (matches.alternative) {
            match = matches.alternative;
            matchType = "alternative";
        } else {
            // No pattern matched - barcode format is not recognized
            console.error("❌ [parseBCBPBarcode2] No matching pattern found");
            setError("Failed to parse flight details");
            return null;
        }

        // ===== STEP 4D: EXTRACT FLIGHT DATA FROM MATCHED PATTERN =====
        // Extract departure and arrival airports (same for all patterns)
        result.departureAirport = match?.[1] ?? "";  // First 3 letters (e.g., "JFK")
        result.arrivalAirport = match?.[2] ?? "";    // Next 3 letters (e.g., "LHR")

        // Extract carrier code and flight number (format varies by pattern)
        if (matchType === "standard") {
            // Standard pattern: carrier code and flight number are space-separated
            let scannedFlightNumber = match?.[0] ?? "";
            if (scannedFlightNumber) {
                scannedFlightNumber = scannedFlightNumber.split(" ");
                // Remove leading zeros from flight number (e.g., "0123" → "123")
                result.flightNumber = scannedFlightNumber?.[1]?.replace(/^0+/, '') ?? "";
                // Get last 2 characters as carrier code (e.g., "JFKLHRBA" → "BA")
                result.carrierCode = scannedFlightNumber?.[0]?.slice(-2) ?? "";
            }
        } else {
            // Numeric carrier and alternative patterns: carrier code and flight number are in capture groups
            result.carrierCode = match?.[3] ?? "";  // Carrier code (e.g., "BA", "4Y", "SV")
            result.flightNumber = match?.[4]?.replace(/^0+/, '') ?? "";  // Flight number without leading zeros
        }

        // ===== STEP 4E: EXTRACT DEPARTURE DATE =====
        // Departure date is stored as Julian date (day of year, 1-366)
        // Example: 123 = May 3rd (123rd day of the year)
        // Pattern: 3 digits followed by an uppercase letter
        let departureDateMatch = rawValue?.match(/\s(\d{3})[A-Z]/);

        if (!departureDateMatch && (matchType === "numericCarrier" || matchType === "letterCarrierAdjacent")) {
            // Alternative pattern for some airlines (e.g., "9999 295" or "9999O295C")
            // Format: 4 digits, optional space or letter, then 3-digit day, then letter
            departureDateMatch = rawValue?.match(/\d{4}[\sA-Z]?(\d{3})[A-Z]/);
        }

        if (departureDateMatch) {
            // Convert Julian date to standard YYYY-MM-DD format
            const departureDay = parseInt(departureDateMatch?.[1], 10);  // Day of year (1-366)
            const currentYear = new Date().getFullYear();  // Assume current year
            const flightDateObj = new Date(currentYear, 0, departureDay);  // Month 0 = January, then add days
            result.departureDate = moment(new Date(flightDateObj)).format("YYYY-MM-DD");
        }

        // ===== STEP 5: EXTRACT ADDITIONAL INFORMATION =====
        // Extract seat assignment and special assistance codes

        // ===== STEP 5A: EXTRACT SEAT NUMBER =====
        // Seat number is embedded in a specific pattern within the barcode
        // Pattern: digits + letter + digits + letter + digits, followed by space and digits with '>'
        // Example: "12345678A0213 377>" → seat is "47B" (characters 5-7 of the match)
        const seatMatch = rawValue?.match(/(\d+[A-Z]\d+[A-Z]\d+)\s+(\d+>)/);
        result.seatNumber = seatMatch ? seatMatch[1].slice(5, 8).trim() : "";

        // ===== STEP 5B: EXTRACT ASSISTANCE CODES =====
        // Special assistance codes indicate passenger needs
        // WCHR = Wheelchair for ramp
        // WCHS = Wheelchair for stairs
        // WCHC = Wheelchair for cabin seat
        // BLND = Blind passenger
        // DEAF = Deaf passenger
        // DPNA = Disabled passenger with intellectual/developmental disability
        // LNYD = Passenger with learning disability
        const assistanceMatch = rawValue?.match(ASSISTANCE_CODE_REGEX);
        const assistanceCode = assistanceMatch ? assistanceMatch[1] : 'None';


        // ===== STEP 6: CREATE AND DISPATCH REDUX OBJECTS =====
        // Create passenger and flight objects to be stored in Redux state

        // ===== STEP 6A: CREATE PASSENGER OBJECT =====
        // This object contains all passenger-specific information
        const passengerObj = {
            firstName: firstName ?? "",
            secondName: secondName ?? "",
            assistance: assistanceCode !== "None" ? [assistanceCode] : "",
            carrierCode: "",
            flightNumber: "",
            referenceNumber: "",
            flightDepartureDate: "",
            dueTime: "",
            prmBio: "",
            originAssistance: true,
            destinationAssistance: true,
            make: "",
            model: "",
            weight: "",
            battery: "",
            width: "",
            height: "",
            equipment: false,
            additionalPassenger: false,
            errors: [],
            seatNumber: ` ${result?.seatNumber ?? ""}`
        };

        // ===== STEP 6B: CREATE FLIGHT OBJECT =====
        // This object contains all flight-specific information
        const flightObj = {
            carrierCode: result?.carrierCode ? result?.carrierCode : "",
            flightNumber: result?.flightNumber ? result?.flightNumber : "",
            flightDepartureDate: result?.departureDate ? result?.departureDate : "",
            errors: [],
        };


        // ===== STEP 6C: DISPATCH TO REDUX STORE =====
        // Update the Redux state with the extracted information
        dispatch(setBookingForm([passengerObj]));      // Update booking form with passenger data
        dispatch(setFlightInformation(flightObj));     // Update flight information

        return result;
    };

    // debounce is used to take the value after onChange finish 
    const handleChange = debounce((event) => {
        // parseBCBPBarcode(event.target.value);
        parseBCBPBarcode2(event.target.value);
    }, 500); // Adjust the delay as needed
    //#endregion

    return (
        <div>
            <Form.Group>
                <Form.Label style={scannerStyles.label}>
                    Click the field below, then use the scanner to auto-fill passenger and flight details
                </Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    onChange={handleChange}
                    ref={textareaRef}
                    placeholder="Scan boarding pass here..."
                    style={scannerStyles.textarea}
                />
            </Form.Group>
            {error && (
                <p style={scannerStyles.error}>{error}</p>
            )}
        </div>
    )
}

const scannerStyles = {
    label: {
        fontSize: 13,
        fontWeight: 400,
        color: Colors.GRAY_DARK,
        marginBottom: 8,
    },
    textarea: {
        height: 72,
        borderRadius: 8,
        fontSize: 14,
        border: `1.5px dashed ${Colors.GRAY_MEDIUM}`,
        background: '#fff',
        resize: 'none',
    },
    error: {
        color: Colors.DARK_RED,
        fontSize: 12,
        marginTop: 6,
        marginBottom: 0,
    },
};

export default HandScanner
